import { useEffect, useState } from 'react';

import { Alert, Button, Group, Modal, Select, Stack, Text, Textarea } from '@mantine/core';

import {
  REGISTRY_ORDER_CLOSE_NOTE_MAX,
  REGISTRY_ORDER_CLOSE_REASONS,
  type RegistryOrder,
  type RegistryOrderCloseReason,
  type RegistryOrderCloseRequest,
} from '@/types/registry-documents';

// Module-level so the Select gets the same array every render.
const REASON_OPTIONS = REGISTRY_ORDER_CLOSE_REASONS.map(({ value, label }) => ({ value, label }));

interface RegistryOrderCloseModalProps {
  opened: boolean;
  onClose: () => void;
  /** The order being closed. Null before the first one is chosen; kept by
   * the caller while the dialog closes, so its title does not blank. */
  order: Pick<RegistryOrder, 'reference' | 'companyName'> | null;
  /** Throws on failure; the modal stays open and shows the message. */
  onConfirm: (request: RegistryOrderCloseRequest) => Promise<unknown>;
}

/**
 * End a sent order that will never be completed, with a reason: a quote
 * that was declined, an order cancelled with the research desk.
 *
 * Closing is bookkeeping in Nexus only. Nothing is sent to Armadillo, so
 * the dialog says to settle it with the desk first. The reason is a closed
 * list so closures can be counted later; `Other` needs a note, which the
 * server enforces too.
 */
export function RegistryOrderCloseModal({
  opened,
  onClose,
  order,
  onConfirm,
}: Readonly<RegistryOrderCloseModalProps>) {
  const [reason, setReason] = useState<RegistryOrderCloseReason | null>(null);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cleared on close, so reopening for another order never inherits a reason.
  useEffect(() => {
    if (!opened) {
      setReason(null);
      setNote('');
      setError(null);
    }
  }, [opened]);

  const noteRequired = reason === 'other';
  const noteMissing = noteRequired && note.trim() === '';
  const canSubmit = reason !== null && !noteMissing && !submitting;

  const submit = async () => {
    if (!reason) {
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm({ reason, note: note.trim() || null });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not close the order');
    } finally {
      setSubmitting(false);
    }
  };

  // A close in flight is not abandoned by dismissing the dialog: its
  // outcome, a refusal above all, must still have somewhere to be shown.
  const dismiss = () => {
    if (!submitting) {
      onClose();
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={dismiss}
      title={order ? `Close order ${order.reference}` : 'Close order'}
    >
      <Stack>
        <Text size="sm">
          {order ? `${order.companyName}: the` : 'The'} order ends here as cancelled. Nothing more
          is expected for it, and the same documents can be ordered again.
        </Text>
        <Alert color="orange" data-testid="registry-order-close-warning">
          Closing does not tell Armadillo anything - cancel or decline with the research desk first.
        </Alert>
        <Select
          label="Reason"
          placeholder="Choose a reason"
          data={REASON_OPTIONS}
          value={reason}
          onChange={(value) => setReason(value as RegistryOrderCloseReason | null)}
          allowDeselect={false}
          comboboxProps={{ withinPortal: false }}
        />
        <Textarea
          label="Note"
          description={noteRequired ? 'Required when the reason is Other' : 'Optional'}
          required={noteRequired}
          autosize
          minRows={2}
          // The API refuses a longer note.
          maxLength={REGISTRY_ORDER_CLOSE_NOTE_MAX}
          value={note}
          onChange={(e) => setNote(e.currentTarget.value)}
        />
        {error && (
          <Alert color="red" title="The order was not closed">
            {error}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={dismiss} disabled={submitting}>
            Keep open
          </Button>
          <Button
            color="red"
            onClick={() => void submit()}
            disabled={!canSubmit}
            loading={submitting}
            data-testid="registry-order-close-confirm"
          >
            Close order
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
